import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Box, Button, Typography, TextField, Chip, CircularProgress } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import StepperButtons from '../acquisition/StepperButtons.jsx';
import ActivityIndicator from '../core/ActivityIndicator.jsx';
import DateInput from '../acquisition/DateInput.jsx';
import ReactGA from 'react-ga';
import moment from 'moment';
import { toISOString, toTimestamp } from '../../../common/utils';
import ee from '../../../services/earth-engine/index.js';
import { ToggleButtonGroup, ToggleButton, Alert } from '@material-ui/lab';
import TimePeriodSelector from '../acquisition/TimePeriodSelector.jsx';
import { useFile, useResult, usePreProcessing } from '../../contexts/index.jsx';
import smoothingImg from '../../../../src/resources/smoothing.png';
import {
  stripSuffix,
  getSelectedSatellite,
  mapGeeError,
  eeEval,
  eeUrl,
  REDUCERS,
  clickDownloadArchive,
} from '../../../algorithms/utils/index.js';
import { FIRST, FINALIZE, NEXT } from '../../../hooks/useStepNavigations.js';
import SimpleInfoDialog from '../dialog/SimpleInfoDialog';
import CompositeImageTable from './CompositeImageTable.jsx';
import SmoothingPreview from './SmoothingPreview.jsx';
import { presetContextOf, presetMismatch } from '../../../algorithms/bathymetry/preset';

/**
 * @function PeriodChooser
 * @description Component responsible for allowing the user to select between a single image or a composite of images for bathymetry data acquisition,
 * as well as to define the time period for the composite if that option is chosen.
 * Single Image - is the image closest to the date of interest (defined in file data) that covers the area of interest.
 * Composite Image - is an image generated by applying a reducer function (e.g., median, mean) over all images that cover the area of interest within a specified time period.
 * For the composite image, the user can select the reducer to apply and the time period to consider.
 * The component also handles the loading state and error handling for interactions with the Google Earth Engine API.
 * @param {Function} navigate - Function to navigate between steps in the acquisition process
 * **Called by:
 * @see {Bathymetry} - bathymetry's acquisition  (index.jsx)
 * @memberof module:Bathymetry
 * @return {JSX.Element} Rendered component for selecting single or composite image and defining composite time period.
 */
const PeriodChooser = ({ navigate }) => {
  const [t] = useTranslation();
  const notifyError = useErrorNotice();
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const selectedCardColor = isDark ? theme.palette.action.selected : '#CCC';
  const { data, setData } = useResult();
  const { fileData, setFileData } = useFile();
  const { generateCollection, collectionRef, satelliteRef, roiRef, createMosaicByCommonId, cleanAndMaskImages } =
    usePreProcessing();
  const periodState = fileData?.periodState;
  const [period, setPeriod] = useState(() => periodState?.period ?? { start: null, end: null });
  const [loading, setLoading] = useState(() => !(periodState?.thumbSingleImg && periodState?.urlDownloadDate));
  const [loadingLabel, setLoadingLabel] = useState('');
  const [showCompositeInterval, setShowCompositeInterval] = useState(() => periodState?.showCompositeInterval ?? false);
  const [thumbSingleImg, setThumbSingleImg] = useState(() => periodState?.thumbSingleImg ?? '');
  const [dateSingleImg, setDateSingleImg] = useState(() => periodState?.dateSingleImg ?? '');
  const [urlDownloadDate, setUrlDownloadDate] = useState(() => periodState?.urlDownloadDate ?? '');
  const [compositeThumbURL, setCompositeThumbURL] = useState(() => periodState?.compositeThumbURL ?? '');
  const [urlDownloadComposite, setUrlDownloadComposite] = useState(() => periodState?.urlDownloadComposite ?? '');
  const [compositeError, setCompositeError] = useState(null);
  const [reducerType, setReducerType] = useState(() => periodState?.reducerType ?? 3);
  const [geomedEpsInput, setGeomedEpsInput] = useState(() => periodState?.geomedEpsInput ?? '1e-3');
  const [applyMosaicSingle, setApplyMosaicSingle] = useState(() => periodState?.applyMosaicSingle ?? false);
  const [applyMosaicComposite, setApplyMosaicComposite] = useState(() => periodState?.applyMosaicComposite ?? false);
  const [datesComposite, setDatesComposite] = useState(() => periodState?.datesComposite ?? []);
  const [errorDialog, setErrorDialog] = useState(null);
  const [smoothRadius, setSmoothRadius] = useState(() => periodState?.smoothRadius ?? 1);
  const [smoothSigma, setSmoothSigma] = useState(() => periodState?.smoothSigma ?? 0.1);
  const imagesRef = useRef(periodState?.images ?? []);
  const compositeCacheRef = useRef({});
  const singleCacheRef = useRef({});
  const listedCollectionRef = useRef(null);
  const singleMissionInfoRef = useRef(periodState?.singleMissionInfo ?? null);
  const [tableState, setTableState] = useState(() => periodState?.tableState ?? 'hidden');
  const [compositeImageList, setCompositeImageList] = useState(() => periodState?.compositeImageList ?? []);
  const [selectedCompositeImages, setSelectedCompositeImages] = useState(
    () => periodState?.selectedCompositeImages ?? {},
  );
  const [fetchedImageCount, setFetchedImageCount] = useState(() => periodState?.fetchedImageCount ?? null);
  const isGeomedSelected = REDUCERS[reducerType]?.label === 'geomed';
  const parsedGeomedEps = Number(geomedEpsInput);
  const isGeomedEpsValid = Number.isFinite(parsedGeomedEps) && parsedGeomedEps > 0;
  const generatedImageRef = useRef(null);
  const [collectionSize, setCollectionSize] = useState(null);
  const type = useMemo(() => window.sessionStorage.getItem('typeSatellite'), []);
  const satelliteInfo = useMemo(() => {
    return type === 'TOA' ? getSelectedSatellite().missions : getSelectedSatellite().surfaceReflectance;
  }, []);

  /**
   * @function missionOfImage
   * @description The mission an image comes from. A Landsat collection merges several missions,
   * told apart by the image's `system:index`; every other satellite has one. Asked for
   * asynchronously: the synchronous read it replaces held the page still for each image looked up.
   * @param {Object} image - The `ee.Image`.
   * **Called by:
   * @see {loadSingleImage}
   * @see {fetchImageList}
   * @see {loadCompositeImage}
   * @memberof module:Bathymetry
   * @returns {Promise<Object>} The mission's entry in `satelliteInfo`.
   */
  const missionOfImage = async (image) => {
    if (getSelectedSatellite().name !== 'Landsat') return satelliteInfo[0];
    const id = await eeEval(image.get('system:index'));
    return satelliteInfo.filter((mission) => id.includes(mission.shortname))[0];
  };
  const imagesInPeriod = useMemo(() => {
    if (!datesComposite.length) return 0;
    if (!period.start || !period.end) return datesComposite.length;
    const s = moment(period.start),
      e = moment(period.end);
    return datesComposite.filter((d) => {
      const m = moment(typeof d === 'number' ? d : d);
      return m.isSameOrAfter(s) && m.isSameOrBefore(e);
    }).length;
  }, [datesComposite, period.start, period.end]);
  const selectedCompositeCount = useMemo(
    () => Object.values(selectedCompositeImages).filter(Boolean).length,
    [selectedCompositeImages],
  );
  const selectedImageDatesList = useMemo(
    () =>
      compositeImageList
        .filter((img) => selectedCompositeImages[img.id])
        .map((img) => img.date)
        .sort(),
    [compositeImageList, selectedCompositeImages],
  );
  const missionSingleComposite = useRef(periodState?.missionSingleComposite ?? { SINGLE: [], COMPOSITE: [] });

  /**
   * @function normalizePeriodValue
   * @description Normalizes the input value for the period to a string in 'YYYY-MM-DD' format.
   * The function can handle strings, numbers (timestamps), and Date objects types.
   * If the input value is falsy (e.g., null, undefined, empty string), it returns null.
   * @param {string|number|Date|null} value - The input value representing a date, which can be a string, a timestamp number, a Date object, or null.
   * **Called by:
   * @see {onChangePeriod}
   * @see {generateComposite}
   * @memberof module:Bathymetry
   * @return {string|null} A string representing the date in 'YYYY-MM-DD' format if the input is valid, or null if the input is falsy.
   */
  const normalizePeriodValue = (value) => {
    return !value ? null : moment(value).format('YYYY-MM-DD');
  };

  /**
   * @function onChangePeriod
   * @description Updates the period state with the new start and end dates.
   * The function also performs validation to ensure that the end date is not before the start date,
   * and if such a case occurs, it alerts the user and does not update the period state.
   * @param {string|number|Date|null} newStart - The new start date.
   * @param {string|number|Date|null} newEnd - The new end date.
   * @see {@link normalizePeriodValue} - normalizes both dates before storing them.
   * **Called by:
   * @see {PeriodChooser} - via the start/end DateInput's onBlur and TimePeriodSelector's onChange.
   * @memberof module:Bathymetry
   * @returns {void} This function does not return a value; it updates the component's state to reflect the new period if the input is valid.
   */
  const onChangePeriod = (newStart, newEnd) => {
    const s = normalizePeriodValue(newStart);
    const e = normalizePeriodValue(newEnd);
    if (s && e && moment(e).isBefore(moment(s))) {
      notifyError(t('forms.acquisition.3.warning'));
      setPeriod({ start: s, end: s });
      return;
    }
    setPeriod({ start: s, end: e });
    setCompositeError(null);
    setFetchedImageCount(null);
    if (tableState === 'ready') setTableState('stale');
  };

  useEffect(() => {
    compositeCacheRef.current = {};
  }, [period.start, period.end]);

  useEffect(() => {
    singleCacheRef.current = {};
  }, [fileData?.date]);

  /**
   * @function loadSingleImage
   * @description Loads a single image from the Earth Engine collection based on the date of interest (from file data) and the area of interest (both from AOI Chooser).
   * 1.The function first checks if the image is already cached to avoid unnecessary API calls.
   * 2.If not cached, it generates the collection for a time window around the date of interest,
   * 3.Cleans and masks the images, and retrieves the image closest to the date of interest.
   * 4. It then generates a thumbnail URL and a download URL for the image, updates the component's state with this information, and caches the results for future use.
   * The function also handles loading state and error handling for interactions with the Earth Engine API.
   * @param {boolean} withMosaic - A flag indicating whether to apply a mosaic operation to the collection before retrieving the image. This can help in cases where multiple images cover the area of interest on the same date.
   * **Called by:
   * @see {PeriodChooser} - on mount, and via the "apply mosaic" checkbox's onChange.
   * @memberof module:Bathymetry
   * @return {void} This function does not return a value; it updates the component's state with the loaded image information and handles caching for performance optimization.
   */
  const loadSingleImage = async (withMosaic = applyMosaicSingle) => {
    const cacheKey = `${fileData?.date}_${withMosaic}`;
    if (singleCacheRef.current[cacheKey]) {
      const c = singleCacheRef.current[cacheKey];
      setThumbSingleImg(c?.thumbURL);
      setUrlDownloadDate(c?.downloadURL);
      setDateSingleImg(c?.date);
      imagesRef.current[0] = c?.image;
      singleMissionInfoRef.current = c?.missionInfo;
      return;
    }
    setLoading(true);
    try {
      if (!fileData?.date) return;
      const targetDate = ee.Date(fileData?.date);
      await generateCollection(
        targetDate.advance(-1, 'year'),
        targetDate.advance(1, 'year'),
        data.landsatChosenCollections,
      );
      await cleanAndMaskImages(type === 'TOA', data.coordinatesSystem, satelliteInfo);
      collectionRef.current = withMosaic ? await createMosaicByCommonId() : collectionRef.current;
      const image = collectionRef.current.first();
      const timestamp = await eeEval(image.get('system:time_start'));
      const date = new Date(timestamp).toISOString().split('T')[0];
      const selectedMission = await missionOfImage(image);
      missionSingleComposite.current.SINGLE.push(selectedMission);
      const [thumbURL, downloadURL] = await Promise.all([
        eeUrl(image, 'getThumbURL', {
          region: roiRef.current,
          bands: [selectedMission.bands.red, selectedMission.bands.green, selectedMission.bands.blue],
          dimensions: 512,
          max: 0.2,
          min: 0.0,
          format: 'png',
        }),
        eeUrl(image, 'getDownloadURL', { region: roiRef.current }),
      ]);
      singleCacheRef.current[cacheKey] = { thumbURL, downloadURL, image, date, missionInfo: selectedMission };
      setThumbSingleImg(thumbURL);
      setUrlDownloadDate(downloadURL);
      setDateSingleImg(date);
      imagesRef.current[0] = image;
      singleMissionInfoRef.current = selectedMission;
    } catch (e) {
      console.error('loadSingleImage error:', e);
      setErrorDialog(mapGeeError(e, t));
    } finally {
      setLoading(false);
    }
  };

  /**
   * @function smoothBandsWater
   * @description Smooths the specified bands in the image based on a water mask.
   * @param {ee.Image} img - The input image.
   * @param {string[]} bands - The list of bands to smooth.
   * @param {number} radiusPx - The radius of the smoothing kernel in pixels.
   * @param {number} smoothSigma - The standard deviation of the Gaussian kernel.
   * **Called by:
   * @see {loadCompositeImage}
   * @memberof module:Bathymetry
   * @return {ee.Image} The image with smoothed bands.
   */
  const smoothBandsWater = (img, bands, radiusPx, smoothSigma) => {
    const waterMask = img.select('waterMask_any');
    const gaussianKernel = ee.Kernel.gaussian({
      sigma: smoothSigma,
      radius: radiusPx,
      units: 'pixels',
      normalize: true,
    });
    const smoothed = img
      .select(bands)
      .reduceNeighborhood({
        reducer: ee.Reducer.mean(),
        kernel: gaussianKernel,
        skipMasked: true,
      })
      .rename(bands)
      .updateMask(waterMask);
    return img.addBands(smoothed, bands, true);
  };

  useEffect(() => {
    if (!satelliteRef || !roiRef) navigate(FIRST);
    if (thumbSingleImg !== '' && urlDownloadDate !== '') return;
    const timer = setTimeout(async () => {
      try {
        await loadSingleImage();
      } catch {
        notifyError(
          'Your account still is not allowed to use GEE API. Complete your register at https://earthengine.google.com/signup/',
        );
        window.location = 'https://earthengine.google.com/signup/';
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  /**
   * @function (sync effect)
   * @description Mirrors every restorable piece of this step's local state into
   * `fileData.periodState` on every change, so that if the user navigates away and back
   * (full unmount/remount, per useStepNavigations), the lazy useState initializers above can
   * restore the form exactly as it was left — single/composite thumbnails, chosen period,
   * reducer/mosaic/smoothing settings, and the composite image-selection table.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  useEffect(() => {
    setFileData((prev) => ({
      ...prev,
      periodState: {
        period,
        showCompositeInterval,
        thumbSingleImg,
        dateSingleImg,
        urlDownloadDate,
        compositeThumbURL,
        urlDownloadComposite,
        reducerType,
        geomedEpsInput,
        applyMosaicSingle,
        applyMosaicComposite,
        datesComposite,
        smoothRadius,
        smoothSigma,
        tableState,
        compositeImageList,
        selectedCompositeImages,
        fetchedImageCount,
        images: imagesRef.current,
        missionSingleComposite: missionSingleComposite.current,
        singleMissionInfo: singleMissionInfoRef.current,
      },
    }));
  }, [
    period,
    showCompositeInterval,
    thumbSingleImg,
    dateSingleImg,
    urlDownloadDate,
    compositeThumbURL,
    urlDownloadComposite,
    reducerType,
    geomedEpsInput,
    applyMosaicSingle,
    applyMosaicComposite,
    datesComposite,
    smoothRadius,
    smoothSigma,
    tableState,
    compositeImageList,
    selectedCompositeImages,
    fetchedImageCount,
  ]);

  /**
   * @function generateComposite
   * @description Generates a composite image by applying a reducer function over all images that cover the area of interest within a specified time period.
   * 1.The function first checks if the composite image for the specified period is already cached to avoid unnecessary API calls.
   * 2. If not cached, it generates the collection for the entire range of available dates, cleans and masks the images, and retrieves the dates of the images in the collection.
   * 3. It then applies the selected reducer function to create the composite image, generates a thumbnail URL and a download URL for the composite, updates the component's state with this information, and caches the results for future use.
   * The function also handles loading state and error handling for interactions with the Earth Engine API.
   * @see {@link normalizePeriodValue} - normalizes the collection's first/last dates into the period state.
   * **Called by:
   * @see {PeriodChooser} - via the composite image card's onClick.
   * @memberof module:Bathymetry
   * @return {void} This function updates the component's state with the generated composite image information.
   */
  const generateComposite = async () => {
    setShowCompositeInterval(true);
    setLoadingLabel(t('forms.core.loading.composite'));
    setLoading(true);
    setCompositeError(null);
    await new Promise((r) => setTimeout(r, 0));
    try {
      if (!period.start || !period.end) {
        const col = await generateCollection(satelliteInfo[0].startYear, '', data.landsatChosenCollections);
        await cleanAndMaskImages(type === 'TOA', data.coordinatesSystem, satelliteInfo);
        const dates = await eeEval(
          col.filterBounds(roiRef.current).sort('system:time_start').aggregate_array('system:time_start'),
        );
        setDatesComposite(dates);
        setPeriod({
          start: normalizePeriodValue(dates[0]),
          end: normalizePeriodValue(dates[dates.length - 1]),
        });
      }
    } catch (error) {
      console.error('generateComposite error:', error);
      setCompositeError(mapGeeError(error, t, 'compositePrepareError'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * @function fetchImageList
   * @description Fetches the list of images from the Earth Engine collection that cover the area of interest within the specified time period.
   * 1. The function first checks if the start and end dates of the period are defined and if the collection reference is available; if not, it returns early.
   * 2. It then applies the date and bounds filters to the collection, retrieves the relevant metadata for each image (such as ID, timestamp, and cloud coverage),
   * and updates the component's state with the list of images and their metadata.
   * 3. Then prepares the data for display in a composite image selection table.
   * The function also handles loading state and error handling for interactions with the Earth Engine API,
   * **Called by:
   * @see {PeriodChooser} - via the "Load/Refresh image list" button's onClick.
   * @memberof module:Bathymetry
   * @returns {void} This function updates the component's state
   */
  const fetchImageList = useCallback(async () => {
    if (!period.start || !period.end || !collectionRef?.current) return;
    const filterStart = moment(period.start).startOf('day').format('YYYY-MM-DD');
    const filterEnd = moment(period.end).add(1, 'day').startOf('day').format('YYYY-MM-DD');
    setTableState('loading');
    setCompositeImageList([]);
    setSelectedCompositeImages({});
    setFetchedImageCount(null);
    try {
      const windowed = collectionRef.current.filterDate(filterStart, filterEnd).filterBounds(roiRef.current);
      const base = (applyMosaicComposite ? createMosaicByCommonId(windowed) : windowed).sort('system:time_start');
      listedCollectionRef.current = base;
      const size = base.size();
      const missionBands = (await missionOfImage(base.first())).bands;
      const rawList = base.toList(size).map((rawImg) => {
        const img = ee.Image(rawImg);
        return ee.Dictionary({
          id: img.get('system:index'),
          ts: img.get('system:time_start'),
          cloudCoverage: img.get('cloudCoverageValue'),
          bands: [missionBands.red, missionBands.green, missionBands.blue],
        });
      });
      const rawData = await eeEval(rawList);
      const allImages = rawData.map(({ id, ts, cloudCoverage, bands }) => {
        const name = satelliteInfo[0].name.includes('LANDSAT')
          ? id.replace(/^[\d_]+/, '')
          : id.replace(/^[0-9_]+(?=\d{8}T)/, '');
        return {
          id: id,
          date: ts ? new Date(ts).toISOString().split('T')[0] : '',
          bands: bands,
          name: name,
          shortname: id.split('_')[0],
          ts,
          content: cloudCoverage ?? -1,
        };
      });
      setCompositeImageList(allImages);
      setFetchedImageCount(allImages.length);
      const initial = {};
      allImages.forEach((img) => {
        initial[img.id] = false;
      });
      setSelectedCompositeImages(initial);
      setTableState('ready');
    } catch (e) {
      console.error('fetchImageList error:', e);
      setCompositeError(mapGeeError(e, t, 'compositeGenerateError'));
      setTableState('hidden');
    }
  }, [collectionRef, roiRef, applyMosaicComposite, createMosaicByCommonId, period.start, period.end, datesComposite]);

  /**
   * @function loadCompositeImage
   * @description Loads the composite image based on the selected reducer and time period.
   * The function first checks if the composite image for the specified parameters is already cached to avoid unnecessary API calls.
   * If not cached, it generates the composite image by applying the selected reducer function to the collection of images that
   * cover the area of interest within the specified time period.
   * The function also handles loading state and error handling for interactions with the Earth Engine API,
   * and updates the component's state with the generated composite image information, including thumbnail and download URLs.
   * @param {string} selReducer - The key of the selected reducer function from the REDUCERS constant.
   * @param {boolean} withMosaic - A flag indicating whether to apply a mosaic operation to the collection before generating the composite.
   * @see {@link smoothBandsWater} - smooths the final composite's bands over the water mask.
   * **Called by:
   * @see {PeriodChooser} - via the "apply mosaic" checkbox's onChange.
   * @see {SmoothingPreview} - via the onGenerate prop.
   * @memberof module:Bathymetry
   * @returns {void} This function updates the component's state with the generated composite image information.
   */
  const loadCompositeImage = async (selReducer = reducerType, withMosaic = applyMosaicComposite) => {
    const cacheKey = `${selReducer}_${withMosaic}_${period.start}_${period.end}_${selectedCompositeCount}`;
    if (compositeCacheRef.current[cacheKey]) {
      const c = compositeCacheRef.current[cacheKey];
      setCompositeThumbURL(c.thumbURL);
      setUrlDownloadComposite(c.downloadURL);
      imagesRef.current[1] = c.image;
      return;
    }
    if (!period.start || !period.end) return;
    if (REDUCERS[selReducer]?.label === 'geomed' && !isGeomedEpsValid) {
      notifyError(t('forms.acquisition.3.bathymetry.invalidGeomedEps'));
      return;
    }
    setCompositeError(null);
    setLoadingLabel(t('forms.core.loading.composite'));
    setLoading(true);
    await new Promise((r) => setTimeout(r, 0));
    try {
      const geomed = (col, first) => {
        const log = col.map((img) => img.toFloat().max(parsedGeomedEps).log());
        const med = log.reduce(ee.Reducer.mean()).toFloat().regexpRename('(.*)_mean', '$1').exp().toFloat();
        return med.copyProperties(first, first.propertyNames());
      };
      const targetStartDate = ee.Date(period.start).advance(-1, 'day');
      const targetEndDate = ee.Date(period.end).advance(1, 'day');
      const collection = collectionRef.current.filterDate(targetStartDate, targetEndDate).filterBounds(roiRef.current);
      const collectionSize = await eeEval(collection.size());
      if (!collectionSize || collectionSize <= 0) return;
      const filterStart = moment(period.start).startOf('day').format('YYYY-MM-DD');
      const filterEnd = moment(period.end).add(1, 'day').startOf('day').format('YYYY-MM-DD');
      const windowed = collectionRef.current.filterDate(filterStart, filterEnd).filterBounds(roiRef.current);
      let col = withMosaic ? createMosaicByCommonId(windowed) : windowed;
      const selIds = compositeImageList.filter((img) => selectedCompositeImages[img.id]).map((img) => img.id);
      if (selIds.length > 0) {
        const filters = selIds.map((id) => ee.Filter.eq('system:index', id));
        col = col.filter(ee.Filter.or(...filters));
      }
      const size = await eeEval(col.size());
      if (!size || size <= 0) throw new Error(t('forms.acquisition.3.bathymetry.noImagesForPeriod'));
      const first = col.first();
      const [missionInfo, bandNames] = await Promise.all([missionOfImage(first), eeEval(first.bandNames())]);
      if (!bandNames?.length) throw new Error(t('forms.acquisition.3.bathymetry.filteredCollectionNoBands'));
      const composite =
        REDUCERS[selReducer]?.label === 'geomed'
          ? ee.Image(geomed(col, first))
          : col.reduce(REDUCERS[selReducer].func());
      const reducedImage =
        REDUCERS[selReducer]?.label === 'geomed' ? composite : stripSuffix(composite, `_${REDUCERS[selReducer].label}`);
      const rBands = await eeEval(reducedImage.bandNames());
      if (!rBands?.length) throw new Error(t('forms.acquisition.3.bathymetry.generatedCompositeNoBands'));
      const urls = Promise.all([
        eeUrl(reducedImage, 'getThumbURL', {
          region: roiRef.current,
          bands: [missionInfo.bands.red, missionInfo.bands.green, missionInfo.bands.blue],
          dimensions: 512,
          max: 0.2,
          min: 0.0,
          format: 'png',
        }),
        eeUrl(reducedImage, 'getDownloadURL', { region: roiRef.current }),
      ]);
      let finalImage = reducedImage;
      const waterAny = collection.select('waterMask_final').reduce(ee.Reducer.max()).rename('waterMask_any');
      const imageReducedWater = ee.Image(reducedImage).addBands(waterAny, null, true);
      const coreBands = [
        missionInfo.bands.blue,
        missionInfo.bands.green,
        missionInfo.bands.red,
        missionInfo.bands.nir,
        missionInfo.bands.swir,
      ].filter(Boolean);
      const optionalBands = ['swir2', 'nir2'].map((b) => missionInfo.bands?.[b]).filter(Boolean);
      const bands = [...coreBands, ...optionalBands];
      finalImage = smoothBandsWater(imageReducedWater, bands, smoothRadius, smoothSigma);
      imagesRef.current = [imagesRef.current[0], reducedImage, finalImage];
      const [thumbURL, downloadURL] = await urls;
      setUrlDownloadComposite(downloadURL);
      setCompositeThumbURL(thumbURL);
      setTimeout(() => {
        generatedImageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
      compositeCacheRef.current[cacheKey] = { thumbURL, downloadURL, image: reducedImage };
      missionSingleComposite.current.COMPOSITE.push(missionInfo);
    } catch (e) {
      console.error('loadCompositeImage error:', e);
      setCompositeThumbURL('');
      setUrlDownloadComposite('');
      setCompositeError(mapGeeError(e, t, 'compositeGenerateError'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * @function handleNext
   * @description Handles the action when the user clicks the "Next" button to proceed to the next step in the acquisition process.
   * 1. Logs the event using ReactGA for Google Analytics, including the selected period.
   * 2. Updates the data in the context with the selected image information, including the URL for downloading the image,
   * the thumbnail URL, the date of the image, and the type of image processing (single or composite).
   * 3. Stores the selected image date in the session storage for use in subsequent steps of the acquisition process.
   * @see {@link selectedImageDatesList} - provides the dates stored as data.selectedImageDates.
   * **Called by:
   * @see {StepperButtons} - via the onNext prop.
   * @see {PeriodChooser} - via the composite card's small "Continue" button.
   * @memberof module:Bathymetry
   * @returns {Boolean|void} `false` when preset coefficients were entered for other imagery than the
   *  one now selected, which keeps the user on this step - see {@link presetMismatch}. Otherwise it
   *  updates the component's state and context with the selected image information and the caller
   *  navigates on.
   */
  const handleNext = () => {
    const mismatch = data.presetMode
      ? presetMismatch(data.presetContext, presetContextOf(getSelectedSatellite(), type, []))
      : null;
    if (mismatch) {
      notifyError(t('forms.acquisition.5.bathymetry.presetMismatch', mismatch));
      return false;
    }
    ReactGA.event({ category: 'Acquisition', action: 'SetPeriod', value: `${period.start}-${period.end}` });
    setData((prev) => ({
      ...prev,
      urlImage: showCompositeInterval ? urlDownloadComposite : urlDownloadDate,
      image: showCompositeInterval && compositeThumbURL !== '' ? imagesRef.current[2] : imagesRef.current[0],
      thumbImage: showCompositeInterval && compositeThumbURL !== '' ? compositeThumbURL : thumbSingleImg,
      imageDate: showCompositeInterval ? `${period.start.slice(0, 10)}/${period.end.slice(0, 10)}` : dateSingleImg,
      typeImageProcess: showCompositeInterval ? 'COMPOSITE' : 'SINGLE',
      selectedImageDates: showCompositeInterval ? selectedImageDatesList : [],
      missionInfo: showCompositeInterval
        ? (missionSingleComposite.current.COMPOSITE[missionSingleComposite.current.COMPOSITE.length - 1] ??
          prev.missionInfo)
        : singleMissionInfoRef.current,
    }));
    let begin = new Date(period.start);
    begin.setDate(begin.getDate() - 1);
    window.sessionStorage.setItem('selectedImageDateFormatted', toISOString(toTimestamp(begin)));
  };

  /**
   * @function handleCheckbox
   * @description Handles the change event for the checkboxes in the composite image selection table.
   * When a checkbox is toggled, this function updates the state that tracks which images are selected for inclusion in the composite.
   * @param {string} key - The unique identifier for the image corresponding to the checkbox that was toggled.
   * @param {boolean} checked - The new checked state of the checkbox (true if checked, false if unchecked).
   * **Called by:
   * @see {CompositeImageTable} - via the onCheckboxChange prop.
   * @memberof module:Bathymetry
   * @returns {void} This function updates the component's state to reflect the new selection of images for the composite.
   */
  const handleCheckbox = useCallback((key, checked) => {
    setSelectedCompositeImages((prev) => ({ ...prev, [key]: checked }));
  }, []);

  /**
   * @function handleSelectAll
   * @description Handles the action when the user clicks the "Select All" checkbox to select or deselect all images in the composite image selection table.
   * @param {string[]} keys - An array of unique identifiers for the images in the selection table.
   * @param {boolean} checked - The new checked state of the "Select All" checkbox (true if checked, false if unchecked).
   * **Called by:
   * @see {CompositeImageTable} - via the onSelectAll prop.
   * @memberof module:Bathymetry
   * @returns {void} This function updates the component's state to reflect the new selection of images for the composite.
   */
  const handleSelectAll = useCallback((keys, checked) => {
    setSelectedCompositeImages((prev) => {
      const next = { ...prev };
      keys.forEach((k) => {
        next[k] = checked;
      });
      return next;
    });
  }, []);

  /**
   * @function Badge
   * @description A functional component that renders a badge element used in the composite image selection table to indicate the status of each image
   * (e.g., whether it is selected, done, or pending). The badge displays a checkmark if the image is marked as "done",
   * the number of the image if it is not done, and applies different styles based on the "done" and "pending" states.
   * @param {Object} props - The properties passed to the Badge component.
   * @param {number} props.n - The number to display in the badge when the image is not marked as "done".
   * @param {boolean} props.done - A flag indicating whether the image is marked as "done". If true, a checkmark will be displayed instead of the number.
   * @param {boolean} props.pending - A flag indicating whether the image is in a "pending" state, which may affect the styling of the badge.
   * **Called by:
   * @see {PeriodChooser} - rendered for each of the three composite wizard steps.
   * @memberof module:Bathymetry
   * @returns {JSX.Element} A styled badge element that visually represents the status of an image in the composite selection process.
   */
  const Badge = ({ n, done, pending }) => (
    <span className={[classes.badge, done ? classes.badgeDone : '', pending ? classes.badgePending : ''].join(' ')}>
      {done ? '✓' : n}
    </span>
  );

  return (
    <Box gap={2}>
      {!errorDialog && loading && (
        <ActivityIndicator forceShow title={loadingLabel || t('forms.core.loading.images')} />
      )}
      <SimpleInfoDialog
        open={!!errorDialog}
        close={() => setErrorDialog(null)}
        title="forms.acquisition.4.bathymetry.errorTitle"
        text={errorDialog}
      />
      <Box className={classes.wrapper} display="flex" alignItems="center" flexDirection="column" gap={2}>
        <Box className={classes.imageSelection}>
          {thumbSingleImg !== '' && (
            <div
              id="bathymetrySingleImage"
              className={classes.imageSelectionCard}
              style={{ backgroundColor: showCompositeInterval ? '' : selectedCardColor }}
              onClick={() => setShowCompositeInterval(false)}>
              <img src={thumbSingleImg} alt="" style={{ width: '250px', height: '300px' }} />
              <a href="#" onClick={async (e) => clickDownloadArchive(e, thumbSingleImg, 'cassie_image.png')}>
                {t('forms.acquisition.3.bathymetry.downloadImage')} (.PNG)
              </a>
              {urlDownloadDate !== '' && (
                <a href="#" onClick={async (e) => clickDownloadArchive(e, urlDownloadDate, 'cassie_image.zip')}>
                  {t('forms.acquisition.3.bathymetry.downloadImage')} (.ZIP)
                </a>
              )}
              <Typography variant="subtitle2">
                {t('forms.acquisition.3.bathymetry.imageDate')}: {dateSingleImg}
              </Typography>
              <Typography variant="subtitle2">
                {t('forms.acquisition.3.bathymetry.dataDate')}: {fileData?.date}
              </Typography>
              <Box display="flex" alignItems="center" mt={1} onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  id="applyMosaicCheck"
                  checked={applyMosaicSingle}
                  disabled={collectionSize !== null && collectionSize < 2}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setApplyMosaicSingle(checked);
                    const cacheKey = `${fileData?.date}_${checked}`;
                    if (!singleCacheRef.current[cacheKey]) {
                      setLoadingLabel(t('forms.core.loading.images'));
                      setLoading(true);
                    }
                    loadSingleImage(checked);
                  }}
                />
                <label htmlFor="applyMosaicCheck" style={{ marginLeft: 6, cursor: 'pointer', fontSize: 13 }}>
                  {t('forms.acquisition.5.bathymetry.applyMosaic')}
                </label>
              </Box>
            </div>
          )}
          <div
            id="bathymetryCompositeImage"
            className={classes.imageSelectionCard}
            style={{ backgroundColor: showCompositeInterval ? selectedCardColor : '' }}
            onClick={() => !showCompositeInterval && generateComposite()}>
            {compositeThumbURL === '' ? (
              <div
                style={{
                  width: '250px',
                  height: '300px',
                  backgroundColor: isDark ? theme.palette.action.hover : '#f5f5f5',
                  border: `2px dashed ${isDark ? theme.palette.divider : '#bdbdbd'}`,
                  borderRadius: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                }}>
                {thumbSingleImg !== '' && (
                  <Typography variant="h6" style={{ marginBottom: 8 }}>
                    {t('forms.acquisition.3.bathymetry.selectIntervalTitle')}
                  </Typography>
                )}
                <Typography variant="body2" style={{ maxWidth: '180px', color: isDark ? theme.palette.text.secondary : '#666', marginBottom: 16 }}>
                  {t('forms.acquisition.3.bathymetry.selectIntervalDescription')}
                </Typography>
                <Button variant="outlined" size="small">
                  {t('forms.acquisition.3.bathymetry.selectPeriodButton')}
                </Button>
              </div>
            ) : (
              <div ref={generatedImageRef}>
                <img
                  src={compositeThumbURL}
                  alt="composite"
                  style={{ width: '250px', height: '300px', backgroundColor: '#9b9b9b' }}
                />
                <br />
                <a href={compositeThumbURL} download="cassie_image.png" target="_blank" rel="noreferrer">
                  {t('forms.acquisition.3.bathymetry.downloadImage')} (.PNG)
                </a>
                <br />
                {urlDownloadComposite !== '' && (
                  <a href={urlDownloadComposite} target="_blank" rel="noreferrer">
                    {t('forms.acquisition.3.bathymetry.downloadImage')} (.ZIP)
                  </a>
                )}
                <Box display="flex" alignItems="center" mt={1} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    id="applyMosaicCompositeCheck"
                    checked={applyMosaicComposite}
                    disabled={selectedCompositeCount < 2}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setApplyMosaicComposite(checked);
                      loadCompositeImage(reducerType, checked);
                    }}
                  />
                  <label htmlFor="applyMosaicCompositeCheck" style={{ marginLeft: 6, cursor: 'pointer', fontSize: 13 }}>
                    {t('forms.acquisition.5.bathymetry.applyMosaic')}
                  </label>
                </Box>
                <Box display="flex" justifyContent="center" style={{ marginTop: 12 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (handleNext() === false) return;
                      navigate(data.presetMode ? FINALIZE : NEXT);
                    }}>
                    {t('forms.acquisition.next')}
                  </Button>
                </Box>
              </div>
            )}
          </div>
        </Box>
        {showCompositeInterval && (
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              padding: '0 24px',
              boxSizing: 'border-box',
            }}>
            <Box className={classes.compositePanel}>
              {compositeError && (
                <Box className={classes.errorContainer}>
                  <Alert
                    severity="error"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => {
                          setCompositeError(null);
                          setCompositeThumbURL('');
                          setUrlDownloadComposite('');
                        }}>
                        {t('forms.acquisition.3.bathymetry.compositeRetry')}
                      </Button>
                    }>
                    {compositeError}
                  </Alert>
                </Box>
              )}
              <Box className={classes.sectionCard}>
                <Box className={classes.sectionHeader}>
                  <Badge n={1} done={tableState === 'ready' || tableState === 'stale'} />
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    {t('forms.acquisition.3.bathymetry.step1Title') || 'Choose reducer and time period'}
                  </Typography>
                </Box>
                <Box className={classes.sectionBody}>
                  <Box display="flex" justifyContent="center" mb={2}>
                    <Box textAlign="center">
                      <Typography variant="body2" style={{ marginBottom: 6, fontWeight: 600 }}>
                        {t('forms.acquisition.3.bathymetry.compositeReducer')}:
                      </Typography>
                      <ToggleButtonGroup
                        value={reducerType}
                        exclusive
                        onChange={(_, v) => {
                          if (v !== null) {
                            setReducerType(v);
                            setCompositeError(null);
                          }
                        }}>
                        {REDUCERS.map((r, i) => (
                          <ToggleButton key={i} value={i}>
                            {r.label}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Box>
                  </Box>
                  {isGeomedSelected && (
                    <Box style={{ maxWidth: 720, margin: '0 auto 16px' }}>
                      <TextField
                        className={classes.geomedField}
                        label="GEOMED_EPS"
                        variant="outlined"
                        size="small"
                        value={geomedEpsInput}
                        onChange={(e) => {
                          setGeomedEpsInput(e.target.value);
                          setCompositeError(null);
                        }}
                        error={!isGeomedEpsValid}
                        helperText={
                          !isGeomedEpsValid
                            ? t('forms.acquisition.3.bathymetry.geomedEpsInvalidHelp')
                            : t('forms.acquisition.3.bathymetry.geomedEpsHelp')
                        }
                      />
                      <Alert severity="warning" style={{ marginTop: 8 }}>
                        {t('forms.acquisition.3.bathymetry.geomedWarning')}
                      </Alert>
                      <Alert severity="warning" style={{ marginTop: 8 }}>
                        {t('forms.acquisition.3.bathymetry.geomedPerformance')}
                      </Alert>
                    </Box>
                  )}
                  <Box display="flex" justifyContent="center" style={{ gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                    <DateInput
                      label={t('forms.acquisition.3.startDate')}
                      value={period.start}
                      onBlur={(v) => onChangePeriod(v, period.end)}
                      charLanguage={t('forms.transectEvolution.chartLanguage')}
                    />
                    <DateInput
                      label={t('forms.acquisition.3.endDate')}
                      value={period.end}
                      onBlur={(v) => onChangePeriod(period.start, v)}
                      charLanguage={t('forms.transectEvolution.chartLanguage')}
                    />
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{ gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <Typography variant="body2" style={{ color: '#555' }}>
                      {t('forms.acquisition.3.bathymetry.imagesInPeriod')}:
                    </Typography>
                    <span className={classes.pill}>
                      {fetchedImageCount !== null ? fetchedImageCount : imagesInPeriod}
                    </span>
                    {tableState !== 'loading' && compositeImageList.length > 0 && (
                      <>
                        <Typography variant="body2" style={{ color: '#555', marginLeft: 8 }}>
                          {t('forms.acquisition.3.bathymetry.imagesSelected') || 'Selected for composite'}:
                        </Typography>
                        <span className={[classes.pill, classes.pillGreen].join(' ')}>{selectedCompositeCount}</span>
                      </>
                    )}
                  </Box>
                </Box>
                <Box style={{ padding: '0 24px 16px', boxSizing: 'border-box', width: '100%' }}>
                  <TimePeriodSelector
                    dates={datesComposite}
                    start={period.start}
                    end={period.end}
                    onChange={onChangePeriod}
                  />
                </Box>
                <Box className={classes.refreshRow}>
                  {tableState === 'stale' && (
                    <Typography variant="caption" style={{ color: '#e65100', marginRight: 'auto' }}>
                      {t('forms.acquisition.3.bathymetry.periodChanged') || 'Period changed — refresh the image list'}
                    </Typography>
                  )}
                  <Button
                    variant={tableState === 'stale' ? 'contained' : 'outlined'}
                    size="small"
                    disabled={tableState === 'loading' || tableState === 'ready' || imagesInPeriod === 0}
                    startIcon={
                      tableState === 'loading' ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />
                    }
                    onClick={fetchImageList}>
                    {tableState === 'stale'
                      ? t('forms.acquisition.3.bathymetry.refreshTable') || 'Refresh image list'
                      : t('forms.acquisition.3.bathymetry.loadImagesButton') || 'Load image list'}
                  </Button>
                </Box>
              </Box>
              {(tableState === 'loading' || tableState === 'ready' || tableState === 'stale') && (
                <Box className={classes.sectionCard}>
                  <Box className={classes.sectionHeader}>
                    <Badge n={2} done={false} pending={tableState === 'loading'} />
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {t('forms.acquisition.3.bathymetry.step2Title') || 'Select images to include in the composite'}
                    </Typography>
                    {tableState !== 'loading' && (
                      <Chip
                        size="small"
                        label={`${selectedCompositeCount} / ${compositeImageList.length}`}
                        color={selectedCompositeCount > 0 ? 'primary' : 'default'}
                        style={{ marginLeft: 'auto' }}
                      />
                    )}
                  </Box>
                  <Box className={classes.tableContainer}>
                    <CompositeImageTable
                      changeTableVisibility={setTableState}
                      images={compositeImageList}
                      selectedImages={selectedCompositeImages}
                      onCheckboxChange={handleCheckbox}
                      onSelectAll={handleSelectAll}
                      t={t}
                      roi={roiRef.current}
                      collectionRef={listedCollectionRef.current ? listedCollectionRef : collectionRef}
                    />
                  </Box>
                </Box>
              )}
              {(tableState === 'ready' || tableState === 'stale') && selectedCompositeCount > 0 && (
                <Box className={classes.sectionCard}>
                  <Box className={classes.sectionHeader}>
                    <Badge n={3} done={compositeThumbURL !== ''} />
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {t('forms.acquisition.3.bathymetry.step3Title')}
                    </Typography>
                  </Box>
                  <Box className={classes.sectionBody}>
                    <SmoothingPreview
                      imageSrc={smoothingImg}
                      radius={smoothRadius}
                      sigma={smoothSigma}
                      onRadiusChange={setSmoothRadius}
                      onSigmaChange={setSmoothSigma}
                      onGenerate={() => loadCompositeImage()}
                      loading={loading}
                      selectedCount={selectedCompositeCount}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </div>
        )}
      </Box>
      <StepperButtons
        navigate={navigate}
        onNext={handleNext}
        nextDisabled={showCompositeInterval && compositeThumbURL === ''}
        nextTarget={data.presetMode ? FINALIZE : NEXT}
      />
    </Box>
  );
};

/**
 * @function useStyles-PeriodChooser
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles .
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Bathymetry
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === 'dark';
  return {
    wrapper: { marginTop: theme.spacing(2) },
    geomedField: { minWidth: 220 },
    errorContainer: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    compositePanel: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
    },
    sectionCard: {
      border: `1px solid ${isDark ? theme.palette.divider : '#e0e0e0'}`,
      borderRadius: 8,
      marginBottom: theme.spacing(3),
      backgroundColor: theme.palette.background.paper,
      overflow: 'hidden',
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 2),
      backgroundColor: isDark ? theme.palette.action.hover : '#f5f5f5',
      borderBottom: `1px solid ${isDark ? theme.palette.divider : '#e0e0e0'}`,
    },
    sectionBody: {
      padding: theme.spacing(3, 4),
    },
    badge: {
      width: 26,
      height: 26,
      borderRadius: '50%',
      backgroundColor: '#4A90D9',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 13,
      fontWeight: 700,
      flexShrink: 0,
    },
    badgeDone: { backgroundColor: '#43a047' },
    badgePending: { backgroundColor: '#bdbdbd' },
    pill: {
      display: 'inline-flex',
      alignItems: 'center',
      backgroundColor: '#4A90D9',
      color: '#fff',
      borderRadius: 12,
      padding: '2px 10px',
      fontSize: 12,
      fontWeight: 700,
    },
    pillGreen: { backgroundColor: '#43a047' },
    sliderRow: { width: '100%' },
    refreshRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 4),
      borderTop: `1px solid ${isDark ? theme.palette.divider : '#f0f0f0'}`,
      backgroundColor: isDark ? theme.palette.action.hover : '#fafafa',
    },
    tableContainer: {
      overflowX: 'auto',
      width: '100%',
    },
    imageSelection: {
      display: 'flex',
    },
    imageSelectionCard: {
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      cursor: 'pointer',
    },
  };
});

export default PeriodChooser;
